/**
 * Thursday Night Fights — tnf-profile.js
 * Place in: assets/tnf-profile.js
 *
 * Requires window.TNF_CONFIG injected by page.tnf-profile.liquid:
 *   supabaseUrl    — your Supabase project URL
 *   supabaseKey    — your Supabase anon/public key
 *   customerName   — Shopify customer full name
 *   customerId     — Shopify customer ID
 *   customerEmail  — Shopify customer email
 */

(function () {
  'use strict';

  const { supabaseUrl, supabaseKey, customerName, customerId, customerEmail } = window.TNF_CONFIG || {};
  if (!customerId) return; // not logged in — nothing to do

  /* ─────────────────────────────────────────────
     SUPABASE CLIENT (shared pattern)
  ───────────────────────────────────────────── */
  async function sbFetch(path, options = {}) {
    const url = supabaseUrl.replace(/\/$/, '') + '/rest/v1' + path;
    const res = await fetch(url, {
      ...options,
      headers: {
        'apikey': supabaseKey,
        'Authorization': 'Bearer ' + supabaseKey,
        'Content-Type': 'application/json',
        'Prefer': options.prefer || 'return=representation',
        ...(options.headers || {}),
      },
    });
    if (!res.ok) {
      const err = await res.text();
      throw new Error('Supabase error ' + res.status + ': ' + err);
    }
    const text = await res.text();
    return text ? JSON.parse(text) : null;
  }

  /* ─────────────────────────────────────────────
     DOM HELPERS
  ───────────────────────────────────────────── */
  const $ = id => document.getElementById(id);

  function showToast(msg) {
    const el = $('tnf-toast');
    el.textContent = msg;
    el.classList.add('tnf-toast--show');
    setTimeout(() => el.classList.remove('tnf-toast--show'), 2800);
  }

  /* ─────────────────────────────────────────────
     STATE
  ───────────────────────────────────────────── */
  let profile = null; // loaded from DB

  /* ─────────────────────────────────────────────
     NOTIFICATION TOGGLE
  ───────────────────────────────────────────── */
  const toggleBtn      = $('tnf-notify-toggle');
  const emailWrap      = $('tnf-notify-email-wrap');
  const discordToggle  = $('tnf-discord-toggle');
  const accessToggle   = $('tnf-accessibility-toggle');

  function setToggle(on) {
    toggleBtn.setAttribute('aria-checked', String(on));
    toggleBtn.classList.toggle('tnf-toggle--on', on);
    emailWrap.style.display = on ? 'block' : 'none';
  }

  function setDiscordToggle(on) {
    discordToggle.setAttribute('aria-checked', String(on));
    discordToggle.classList.toggle('tnf-toggle--on', on);
  }

  function setAccessToggle(on) {
    accessToggle.setAttribute('aria-checked', String(on));
    accessToggle.classList.toggle('tnf-toggle--on', on);
  }

  toggleBtn.addEventListener('click', () => {
    const current = toggleBtn.getAttribute('aria-checked') === 'true';
    setToggle(!current);
  });

  discordToggle.addEventListener('click', () => {
    const current = discordToggle.getAttribute('aria-checked') === 'true';
    setDiscordToggle(!current);
  });

  accessToggle.addEventListener('click', () => {
    const current = accessToggle.getAttribute('aria-checked') === 'true';
    setAccessToggle(!current);
  });

  /* ─────────────────────────────────────────────
     LOAD GAME SYSTEMS INTO SELECT
  ───────────────────────────────────────────── */
  async function loadGameSystems() {
    const sel = $('tnf-profile-game');
    try {
      const systems = await sbFetch('/tnf_game_systems?active=eq.true&order=display_order.asc,name.asc');
      if (!systems || systems.length === 0) {
        sel.innerHTML = '<option value="">No game systems configured yet</option>';
        return;
      }
      sel.innerHTML = '<option value="">— select a game —</option>' +
        systems.map(gs => `<option value="${gs.id}">${gs.name}</option>`).join('');
    } catch (e) {
      console.error('TNF profile: failed to load game systems', e);
    }
  }

  /* ─────────────────────────────────────────────
     LOAD EXISTING PROFILE
  ───────────────────────────────────────────── */
  async function loadProfile() {
    try {
      const rows = await sbFetch('/tnf_player_profiles?customer_id=eq.' + customerId + '&limit=1');
      if (rows && rows.length > 0) {
        profile = rows[0];
        // Populate game system
        if (profile.favourite_game_id) {
          $('tnf-profile-game').value = String(profile.favourite_game_id);
        }
        // Populate notification settings
        const notifyOn = profile.notify_email || false;
        setToggle(notifyOn);
        if (profile.notification_email) {
          $('tnf-notify-email').value = profile.notification_email;
        }
        // Populate Discord
        if (profile.discord_handle) {
          $('tnf-profile-discord').value = profile.discord_handle;
        }
        setDiscordToggle(profile.show_discord !== false); // default true
        setAccessToggle(profile.accessibility === true);  // default false
      }
    } catch (e) {
      console.error('TNF profile: failed to load profile', e);
    }
  }

  /* ─────────────────────────────────────────────
     SAVE PROFILE
  ───────────────────────────────────────────── */
  const saveBtn = $('tnf-profile-save-btn');
  if (saveBtn) {
    saveBtn.addEventListener('click', async () => {
      const gameId        = $('tnf-profile-game').value;
      const notifyOn      = $('tnf-notify-toggle').getAttribute('aria-checked') === 'true';
      const notifyEmail   = $('tnf-notify-email').value.trim() || customerEmail;
      const discordHandle  = $('tnf-profile-discord').value.trim();
      const showDiscord    = $('tnf-discord-toggle').getAttribute('aria-checked') === 'true';
      const accessibility  = $('tnf-accessibility-toggle').getAttribute('aria-checked') === 'true';

      if (notifyOn && !notifyEmail) {
        showToast('Please enter a notification email');
        return;
      }

      saveBtn.disabled    = true;
      saveBtn.textContent = 'Saving…';

      const payload = {
        customer_id:        String(customerId),
        customer_name:      customerName,
        notify_email:       notifyOn,
        notification_email: notifyOn ? notifyEmail : null,
        favourite_game_id:  gameId ? parseInt(gameId, 10) : null,
        discord_handle:     discordHandle || null,
        show_discord:       showDiscord,
        accessibility:      accessibility,
        updated_at:         new Date().toISOString(),
      };

      try {
        if (profile) {
          // Update existing
          await sbFetch('/tnf_player_profiles?customer_id=eq.' + customerId, {
            method: 'PATCH',
            prefer: 'return=minimal',
            body: JSON.stringify(payload),
          });
        } else {
          // Insert new
          const created = await sbFetch('/tnf_player_profiles', {
            method: 'POST',
            body: JSON.stringify(payload),
          });
          if (created && created[0]) profile = created[0];
        }
        showToast('Profile saved ✓');
        const banner = $('tnf-profile-saved-banner');
        if (banner) {
          banner.style.display = 'block';
          setTimeout(() => { banner.style.display = 'none'; }, 3000);
        }
      } catch (e) {
        showToast('Save failed — please try again');
        console.error('TNF profile save error:', e);
      } finally {
        saveBtn.disabled    = false;
        saveBtn.textContent = 'Save Profile';
      }
    });
  }

  /* ─────────────────────────────────────────────
     BOOT — load game systems then existing profile
  ───────────────────────────────────────────── */
  loadGameSystems().then(loadProfile);

})();
